import Component from '@glimmer/component';
import { A } from '@ember/array';
import { action, set, getProperties } from '@ember/object';
import { scheduleOnce, later, next } from '@ember/runloop';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { addObserver, removeObserver } from '@ember/object/observers';

import PostApi from 'mewe/api/post-api';
import DocumentsApi from 'mewe/api/documents-api';
import PurchasesStore from 'mewe/stores/purchases-store';
import FunctionalUtils from 'mewe/shared/functional-utils';
import Mentions from 'mewe/utils/mentions-utils';
import HashtagsUtils from 'mewe/utils/hashtags-utils';
import PostUtils from 'mewe/utils/post-utils';
import PostCommons from 'mewe/utils/posting-utils';
import PostStatus from './controllers/status';
import PostPhoto from './controllers/photo';
import PostDocument from './controllers/document';
import PostVideo from './controllers/video';
import PostPoll from './controllers/poll';
import PostLink from './controllers/link';
import { isMeWeTeam } from 'mewe/helpers/is-mewe-team';
import toServer from 'mewe/stores/text-parsers/to-server';
import toEdit from 'mewe/stores/text-parsers/to-edit';
import { Theme, Target, TargetContact, maxHashtags } from 'mewe/constants';
import { widthHideSidebars } from 'mewe/constants-layout';
import config from 'mewe/config';
import PS from 'mewe/utils/pubsub';
import { ds } from 'mewe/stores/ds';
import TwitterText from 'twitter-text';
import PagesApi from 'mewe/api/pages-api';
import { stubTrue, cond, matches } from 'lodash';
import postboxDebug from './postbox-debug';
import Verbose from 'mewe/utils/verbose';
import GroupStore from 'mewe/stores/group-store';
import PermissionsUtils from 'mewe/utils/group-permissions-utils';
import dispatcher from 'mewe/dispatcher';
import { setPostboxOpen } from 'mewe/utils/scrolling-utils';
import { installDropdown } from 'mewe/utils/dropdown-utils';

const gt = (v, o) => v > o;

const placeholder = {
  photo: (v) => () => __('Say something about this {count} photo', { count: v.count }), // { count: } needed or i18n extractor lib doesn't understand it's a plural
  poll: () => __('Please answer my question!'),
  video: () => __('Say something about this video'),
  page: () => __('Share a post with your followers'),
  event: () => __('Post to this event'),
  mycloud: () => __('Add photos and files to My Cloud'),
  groups: () => __('Share something with one of your groups'),
  group: () => __('Post to this group'),
  contacts: () => __('How is your day going?'),
  profile: () => __('How is your day going?'),
};

const verbose = Verbose({ prefix: '[Postbox]' });
const log = verbose.log;

const POSTBOX_HEADER_CLASS = '.postbox_editor_textarea';

class PhotoPostWrapper {
  @tracked photos = A();
  @tracked loading = null;
}

class VideoPostWrapper {
  @tracked loading = null;
  @tracked progress = null;
  @tracked fail = null;
}

class PollSettingsWrapper {
  @tracked days = A();
  @tracked hours = A();
  @tracked minutes = A();
  @tracked maxOptions = null;
  @tracked selectedDays = 0;
  @tracked selectedHours = 0;
  @tracked selectedMinutes = 0;
}

// after refactoring postbox to glimmer please check for conditions to be removed in 3 places in 'controllers/link'
export default class MwPostbox extends Component {
  @service router;
  @service analytics;
  @service account;
  @service dynamicDialogs;
  @service dynamicPopups;
  @service('postboxUpload') uploadService;

  // - potentially unused, cant find it in code
  // upload = false;
  // canPost = null;

  maxTextLimit = config.maxTextLimit;

  // general
  @tracked theme = this.args.theme;
  @tracked newPost = '';
  @tracked postingType;
  @tracked includeOriginalPost = true;
  @tracked isSharing = false;
  @tracked commentsDisabled = false;
  @tracked dropdownSharing;
  @tracked dropdownMedia;
  @tracked postboxOpenedWithoutTarget;
  @tracked selectedTargetId;
  @tracked administrated;
  @tracked storeState;
  @tracked presetEmojis = A();
  @tracked isPublic = false;
  @tracked scheduleTimestamp;
  @tracked autofocusEditor;
  @tracked _scheduleVisible;
  @tracked _selectedAlbumName;
  @tracked _selectedGroup;
  @tracked _selectedEvent;
  @tracked _selectedPage;

  // text
  @tracked editor;
  @tracked selectedTags = A();
  @tracked mentionsStrategy = null;
  @tracked hashtagsStrategy = null;

  // attachments
  @tracked sticker;
  @tracked selectedGifs = A();
  @tracked isLoadingGfyLinkInfo;

  // link
  @tracked link;
  @tracked isLoadingLink;

  // video
  @tracked video = new VideoPostWrapper();
  @tracked videoPreviewSrc;

  // photo
  @tracked photo = new PhotoPostWrapper();

  // poll
  @tracked showPoll = false;
  @tracked showPollLength = false;
  @tracked isPollValid = false;
  @tracked pollQuestionError = false;
  @tracked pollSettings = new PollSettingsWrapper();
  @tracked pollQuestion = '';
  @tracked pollOptions = A();

  // audio
  @tracked voiceRecordingVisible;
  @tracked audioUploaded;
  @tracked audioRecordingUrl;
  @tracked audioRecordingBlob;
  @tracked isAudioConverting;
  @tracked isAudioRecording;
  @tracked isAudioUploading;
  @tracked preselectedAudio;

  // files
  @tracked documents = A();
  @tracked hasDocumentsInProgress = false;
  @tracked preselectedFiles = A();
  @tracked _folder;

  premiumPreviewTrackingContext = 'post_scheduling';
  isStickerPickerOpen = false;

  get group() {
    return this._selectedGroup ?? this.args.preselectedGroup;
  }
  get event() {
    return this._selectedEvent ?? this.args.preselectedEvent;
  }
  get page() {
    return this._selectedPage ?? this.args.preselectedPage;
  }
  get groupId() {
    return this.group?.id;
  }
  get eventId() {
    return this.event?.id;
  }
  get pageId() {
    return this.page?.id;
  }
  get target() {
    return this.args.target;
  }
  get postToShare() {
    return this.args.postToShare;
  }
  get postToRepost() {
    return this.args.postToRepost;
  }

  constructor() {
    super(...arguments);

    this.dynamicDialogs.registerCloseFunction(this.args.dialogId, this.confirmClose);

    this.storeState = PurchasesStore.getState();

    this.autofocusEditor = window.innerWidth > widthHideSidebars;

    this.uploadService.postboxOpened = true;

    installDropdown(this, 'dropdownSharing', { refFunctionName: 'setDropdownRefSharing' });
    installDropdown(this, 'dropdownMedia', { refFunctionName: 'setDropdownRefMedia' });

    setPostboxOpen(true);

    this.newPostHandler = this._newPostHandler.bind(this);
    PS.Sub('post.add', this.newPostHandler);

    if (verbose.isEnabled) {
      postboxDebug(this, log);
    }

    if (this.postToRepost) {
      this.newPost = toEdit(this.postToRepost.textServer).trim();

      if (this.postToRepost.medias?.length) {
        if (this.postToRepost.medias[0].video) {
          this.preselectedVideo = this.postToRepost.medias[0].mediaId;
          this.uploadService.prefillVideo(this.preselectedVideo);
        } else {
          if (this.postToRepost.mediasCount > 4) {
            PostApi.getMultiPhoto(this.theme, this.postToRepost).then((data) => {
              if (this.isDestroying || this.isDestroyed) return;

              this.photo.photos.pushObjects(
                data.medias.map((photo) => {
                  return {
                    id: photo.photo.id,
                    tempId: photo.photo.id,
                    existing: true,
                    orientation: 'normal',
                    url: photo.photo._links.img.href.replace('{imageSize}', '400x400').replace('{static}', '1'),
                    isUrl: true,
                    mediaId: photo.mediaId,
                  };
                }),
              );

              this.uploadService.prefillImages(this.photo.photos);
            });
          } else {
            this.photo.photos.pushObjects(
              this.postToRepost.medias.map((photo) => {
                let inconsistenUrl =
                  photo.imgUrl ||
                  photo.imageUrl ||
                  photo.photo.imageLink ||
                  (photo.photo && photo.photo._links && photo.photo._links.img
                    ? photo.photo._links.img.href.replace('{imageSize}', '800x800').replace('{static}', '1')
                    : null);

                return {
                  id: photo.photo.id,
                  tempId: photo.photo.id, // need to be able to change order of photos in postbox (MW)
                  existing: true,
                  orientation: 'normal',
                  url: inconsistenUrl,
                  isUrl: true,
                  mediaId: photo.mediaId,
                };
              }),
            );

            this.uploadService.prefillImages(this.photo.photos);
          }
        }
      }

      if (this.postToRepost.sticker) {
        this.sticker = this.postToRepost.sticker;
      }

      if (this.postToRepost.gifUrls?.length) {
        this.selectedGifs = A(this.postToRepost.gifUrls);
      }

      // only if there is 1 file and it's audio, if multiple files and first audio then whole post's type is multi-file
      if (this.postToRepost.files.length === 1 && this.postToRepost.files[0].isAudio) {
        this.prefillAudio(this.postToRepost.files[0]);
      }
    }

    if (this.args.preselectedPhotos?.length) {
      next(() => {
        this.photo.photos.pushObjects(this.args.preselectedPhotos);
      });
    }

    if (this.args.preselectedFiles?.length) {
      this.preselectedFiles = this.args.preselectedFiles;
      this.uploadService.prefillFiles(this.preselectedFiles);
    }

    // reposting audio file from mycloud MW-2408
    if (this.args.preselectedAudio) {
      this.prefillAudio(this.args.preselectedAudio);
    }

    if (this.args.newPostPrefilled) {
      this.newPost = this.args.newPostPrefilled;
    }

    this.postingType = this.account.activeUser.public ? TargetContact.PUBLIC : TargetContact.PRIVATE;

    this.postboxOpenedWithoutTarget = !this.targetEntity();

    if (this.target === Target.PAGE && !this.page) {
      this.administrated = ds.administrated;
    } else if (this.isPostingToEvent && !this.postToRepost && !this.postToShare && !this.args.preselectedEvent) {
      // EVENT theme will effect in posting to this event, if there is any group passed to postbox then do not set eventId
      const rootEvent = this.args.eventState?.rootEvent;
      if (rootEvent?.id) this._selectedEvent = rootEvent;
    }

    window.onbeforeunload = (e) => this.onBeforeUnload(e);

    this.setAutocomplete();
    this.updateHeight();
  }

  @action
  onInsert(element) {
    this.element = element;

    this.afterRender();

    this.postStatus = new PostStatus(this);
    this.postPhoto = new PostPhoto(this);
    this.postVideo = new PostVideo(this);
    this.postDocument = new PostDocument(this);
    this.postPoll = new PostPoll(this);
    this.linkController = new PostLink(this);

    this.uploadService.setControllers(this.postPhoto, this.postDocument, this.postVideo);

    scheduleOnce('afterRender', this, () => {
      if (this.isDestroying || this.isDestroyed) return;

      if (this.newPost) {
        this.linkController.parseLink(); // might be a link so try to parse
      }

      // posting/sharing to timeline doesn't require selecting target entity (Timeline is a target itself).
      // sharing to any other destination requires target entity selection if it's not preselected
      //  FOR NOW DISABLED AUTO OPENING OF DROPDOWN, testing if that's better UX
      // if (this.target !== Target.CONTACTS) {
      //   const isTargetSelected = this.targetEntity();
      //   this.setDropdownRefSharing(this.postToShare || this.postToRepost || !isTargetSelected);
      // }

      if (this.args.openWithPhotoBrowser) {
        this.addPhoto();
      } else if (this.args.openWithVideoBrowser) {
        this.addVideo();
      } else if (this.args.openWithFileBrowser) {
        this.addFile();
      }
    });

    this.args.sendPostboxToParent?.(this);

    if (this.args.preselectedFilesToUpload) {
      this.uploadService.add(this.args.preselectedFilesToUpload);
    }

    if (this.postToRepost?.link && !this.postToRepost?.medias?.length) {
      this.linkController.fetchLinkInfo(this.postToRepost.link._links.url.href);
    }

    if (this.postToShare?.link) {
      this.linkController.fetchLinkInfo(this.postToShare.link._links.url.href);
    }

    addObserver(this, 'showAddToAlbum', this.openAlbumPicker);
    addObserver(this, 'audioRecordingBlob', this.audioRecordingBlobChanged);
    addObserver(this, 'canSchedule', this.canScheduleChange);
  }

  @action
  onDestroy() {
    if (this.pasteHandlerBind) {
      this.element.querySelector(POSTBOX_HEADER_CLASS).removeEventListener('paste', this.pasteHandlerBind);
    }

    document.removeEventListener('dragover', this.dragoverHandlerBind);
    this.element.removeEventListener('drop', this.dropHandlerBind);

    window.onbeforeunload = null;

    setPostboxOpen(false);

    if (typeof this.postingController.cleanAfter === 'function') {
      this.postingController.cleanAfter(this);
    }

    this.uploadService.clear();

    PS.Unsub('post.add', this.newPostHandler);

    removeObserver(this, 'showAddToAlbum', this.openAlbumPicker);
    removeObserver(this, 'audioRecordingBlob', this.audioRecordingBlobChanged);
    removeObserver(this, 'canSchedule', this.canScheduleChange);
  }

  toggleVideoPreview(event) {
    const video = event.target;
    if (video.paused) {
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          console.error('Video playback error:', error);
        });
      }
    } else {
      video.pause();
    }
  }

  @action
  toggleDropdownSharing(value) {
    if (this.target === Target.MYCLOUD) return;
    this.setDropdownRefSharing(value);
  }

  @action
  inputPhotoChange() {
    const inputPhoto = this.element.querySelector('#fake-upload-photo');

    this.uploadService.add(inputPhoto.files);
    inputPhoto.value = null;
  }

  @action
  inputPhotoPollChange() {
    const inputPhotoPoll = this.element.querySelector('#fake-upload-photo-poll');
    this.postPoll.handlePhoto({ file: inputPhotoPoll.files[0], index: this.currentPollPhotoIndex });
  }

  @action
  inputVideoChange() {
    const inputVideo = this.element.querySelector('#fake-upload-video');

    this.uploadService.add(inputVideo.files);
    inputVideo.value = null;
  }

  @action
  inputDocumentChange() {
    const inputDocument = this.element.querySelector('#fake-upload-document');

    this.uploadService.add(inputDocument.files);
    inputDocument.value = null;
  }

  @action
  removeGif(gif) {
    this.selectedGifs = A(this.selectedGifs.filter((g) => g !== gif));
    this.updateHeight();
  }

  setAutocomplete() {
    let targetEntity = this.targetEntity(),
      targetId = targetEntity ? targetEntity.value : null,
      strategyParam;

    const isNotMyProfile = this.theme === Theme.PROFILE && !this.args.user?.isMe;

    if (targetEntity) {
      strategyParam = targetEntity.hash;
    } else if (isNotMyProfile) {
      strategyParam = this.args.user?.isMe ? Theme.CONTACTS : Theme.PROFILE;
    } else if (this.target === Target.CONTACTS) {
      strategyParam = Target.CONTACTS;
    }

    // when sharing post to group/page/event/PP - we can't autocomplete
    // mentions/hashtags until some sharing target is selected
    const isSharing = this.postToRepost || this.postToShare;
    if (isSharing && !targetId && this.target !== Target.CONTACTS) return;
    // no autocomplete when posting in /groups scope but group was not selected
    if (!isSharing && !targetId && this.theme === Theme.GROUPS) return;

    // no mentions for pages
    if (strategyParam && !this.page) {
      this.mentionsStrategy = Mentions.createTextCompleteStrategy(
        strategyParam,
        this.postToRepost,
        isNotMyProfile ? this.args.user : null,
      );
    }

    this.hashtagsStrategy = HashtagsUtils.getHashtagStrategy(this.target, targetId);
  }

  get scheduleVisible() {
    return this._scheduleVisible ?? (this.args.scheduleVisible || false);
  }

  get selectedAlbumName() {
    return this._selectedAlbumName ?? (this.args.selectedAlbumName || '');
  }

  get showAlbumFavoritesWarning() {
    return this.postingType === 'friends' && this.selectedAlbumName;
  }

  get isPostingToEvent() {
    return (this.theme === Theme.EVENT || this.theme === Theme.EVENTS) && !this.group;
  }

  get shareButtonText() {
    if (this.target === Target.MYCLOUD || this.postToRepost?.isEditing) {
      return __('Save');
    }

    if (this.scheduleTimestamp) {
      return __('Schedule');
    } else {
      return __('Post');
    }
  }

  get postboxStyle() {
    if (this.showVideo) {
      return 'video';
    }

    if (this.documents.length || this.preselectedFiles?.length) {
      return 'documents';
    }
  }

  get shareButtonDisabled() {
    // photos uploading
    if (this.photo.loading) return true;

    // audio converting
    if (this.isAudioConverting || this.isAudioRecording || this.isAudioUploading) return true;

    // is sharing
    if (this.isSharing) return true;

    // doc uploading
    if (this.hasDocumentsInProgress) return true;

    // video uploading
    if (this.video.loading === true || (this.video.progress > 0 && this.video.progress < 100)) return true;

    // target is correctly set (eg. choosen group or page, choosen mycloud)
    let targetNotReady = [Target.CONTACTS, Target.MYCLOUD].includes(this.target) ? false : !this.targetEntity();

    if (targetNotReady) return true;

    if (this.scheduleTimestamp) {
      const scheduledInPast = !isNaN(new Date(this.scheduleTimestamp).getTime()) && Date.now() > this.scheduleTimestamp;
      if (scheduledInPast) return true;
    }

    // edited post can have crap removed from it
    if (this.postToRepost?.isEditing) return false;

    let thereArePreselectedFiles = this.preselectedFiles?.length;

    let thereArePhotos = this.photo.photos?.length;

    let thereAreLoadedDocuments = this.documents.filter((el) => el.progress === 100).length;

    let thereIsVideoLoaded = this.video.progress == 100 && this.video.loading === false;

    let thereIsLink = this.link;

    const thereArePhotosLoaded = this.photo.photos?.filter((el) => el.loading === false).length;

    if (
      thereArePhotosLoaded ||
      thereIsLink ||
      thereIsVideoLoaded ||
      thereAreLoadedDocuments ||
      thereArePhotos ||
      thereArePreselectedFiles ||
      this.postToShare ||
      toServer(this.newPost, { parseNativeMarkdown: true }) ||
      this.sticker ||
      this.selectedGifs.length ||
      this.audioUploaded ||
      (this.showPoll && this.isPollValid) ||
      this.preselectedVideo ||
      this.selectedTags.length ||
      this.preselectedAudio
    )
      return false;

    return true;
  }

  // allow link scrapping when there are images
  get allowLinkScrapping() {
    if (this.photo.photos?.length) {
      return true;
    }

    return !this.hasSomeAttachments && !this.isUploadItemMode;
  }

  get hasSomeAttachments() {
    if (this.showPoll || this.preselectedFiles?.length) {
      return true;
    }

    return false;
  }

  get isMeWe() {
    return isMeWeTeam() || this.account.activeUser.id === '5602c780e4b08f388c897a39';
  }

  get arePresetEmojisAvailable() {
    if (this.args.myCloudUploading) return false;

    return this.isMeWe && !this.postToRepost?.isEditing && this.target !== Theme.MYCLOUD;
  }

  // format ready to use for param in post/reshare request: array of strings
  get presetEmojisList() {
    return this.presetEmojis.map((e) => e.unicode || e.shortname);
  }

  get showControlsButtons() {
    return this.args.setShowControlsButtons === false ? false : !this.hasSomeAttachments;
  }

  get showFilesButton() {
    if (this.preselectedFiles?.length) {
      return true;
    }

    if (this.args.setShowFilesButton !== undefined) return this.args.setShowFilesButton;

    return false;
  }

  get folder() {
    return this._folder ?? this.args.folder;
  }

  @action
  openAlbumPicker() {
    if (this.showAddToAlbum && !this.albumAlreadyShown) {
      if (this.args.openWithAlbumDropdown) {
        this.albumAlreadyShown = true;
        scheduleOnce('afterRender', () => {
          if (this.isDestroying || this.isDestroyed) return;
          this.element.querySelector('.album-picker .dropdown-menu_opener').click();
        });
      }
    }
  }

  @action
  audioRecordingBlobChanged() {
    const blob = this.audioRecordingBlob;

    if (blob) {
      this.isAudioUploading = true;

      this.audioUploadingXhr = DocumentsApi.uploadBlobAudio(blob);

      this.audioUploadingXhr
        .then((data) => {
          if (data.files && data.files[0]) {
            this.audioUploaded = true;
            this.audioFileId = data.files[0].id;
          }

          log('audio upload complete');
        })
        .catch((data) => {
          if (data.data && data.data.errorCode === 700) {
            this.dynamicDialogs.openDialog('store-item-storage-dialog', { storageAlert: true });
          } else {
            FunctionalUtils.showDefaultErrorMessage();
          }
          this.cancelVoice();
        })
        .finally(() => {
          this.isAudioUploading = false;
        });
    } else {
      this.audioUploaded = null;
      this.audioFileId = null;
    }
  }

  get showAddToAlbum() {
    return this.target !== Theme.EVENT && (this.showVideo || this.showPhoto);
  }

  get showVideo() {
    return this.video.loading || this.video.progress === 100 || this.preselectedVideo;
  }

  get showPhoto() {
    return this.photo.photos?.length;
  }

  get showDocuments() {
    return this.documents.length || this.preselectedFiles?.length;
  }

  get showAttachments() {
    return this.showVideo || this.showPhoto || this.showDocuments || this.sticker || this.selectedGifs.length;
  }

  // LINK scrapping vvvv
  parseTextValueKey = 'newPost';
  convertGifUrlFromLinks = true;

  get showLinkPreview() {
    return (this.isLoadingLink || this.link) && !this.postToShare;
  }

  canDoLinkScrapping() {
    // you cannot have link preview, if you're not in status mode
    return this.allowLinkScrapping && !this.postToShare?.link;
  }
  // LINK scrapping ^^^^

  get getTheme() {
    if (this.theme === Theme.CONTACTS || this.theme === Theme.PROFILE) {
      return 'myworld';
    } else {
      return this.theme;
    }
  }

  get postboxPlaceholder() {
    const postingScope = this.target;

    const getText = cond([
      [matches({ showPoll: true }), placeholder.poll],
      [
        matches({ 'args.myCloudUploading': true, 'args.openWithPhotoBrowser': true }),
        placeholder.photo({ count: this.photo.photos?.length }),
      ],
      [matches({ 'args.myCloudUploading': true, 'args.openWithPhotoBrowser': false }), placeholder.video],
      [stubTrue, placeholder[postingScope] || placeholder.contacts],
    ]);

    return getText(getProperties('showPoll', 'args.myCloudUploading', 'args.openWithPhotoBrowser'));
  }

  afterRender() {
    // Disable D&D if post is being shared
    if (!this.postToShare) {
      this.dragoverHandler = (e) => {
        const files = e.dataTransfer;
        e.stopPropagation();
        e.preventDefault();
        if (files && files.items && files.items[0] && files.items[0].kind !== 'file') {
          e.preventDefault();
          return;
        }
        if (!this.isDestroyed && !this.isDestroying) {
          this.initDropzones();
        }
      };

      this.dropHandler = (ev) => {
        ev.stopPropagation();
        ev.preventDefault();
        this.uploadService.add(ev.dataTransfer.files);
      };

      this.pasteHandler = (ev) => {
        if (ev.clipboardData && ev.clipboardData.files && ev.clipboardData.files[0] && !this.showPoll) {
          this.uploadService.add(ev.clipboardData.files);
        }

        // this is needed to prevent the link from being parsed before the paste event is handled
        later(() => {
          this.linkController.parseLink();
        }, 5);
      };
      this.pasteHandlerBind = this.pasteHandler.bind(this);
      let el = this.element.querySelector(POSTBOX_HEADER_CLASS);
      if (el) el.addEventListener('paste', this.pasteHandlerBind);
    } else {
      this.dragoverHandler = (e) => {
        e.stopPropagation();
        e.preventDefault();
      };

      this.dropHandler = (ev) => {
        ev.stopPropagation();
        ev.preventDefault();
      };
    }

    //when drop is disabled, there is still need to prevent other handlers to run
    this.dragoverHandlerBind = this.dragoverHandler.bind(this);
    document.addEventListener('dragover', this.dragoverHandlerBind);

    this.dropHandlerBind = this.dropHandler.bind(this);
    this.element.addEventListener('drop', this.dropHandlerBind);
  }

  canUploadNonImage() {
    // can upload non-image if there is no image uploaded yet
    if (!this.photo.photos?.length) {
      return true;
    } else {
      return false;
    }
  }

  get isUploadItemMode() {
    return this.showVideo || this.photo.photos?.length || this.documents.length;
  }

  get showAnyMediaButton() {
    return this.showAddMediaButton ||
      this.showAddFileButton ||
      this.showAddPollButton ||
      this.showVoiceRecordButton ||
      this.showAddGifButton ||
      this.showAddStickerButton ||
      this.showScheduleButton;
  }

  get showAddMediaButton() {
    // some photos are already added so user can add more
    if (this.showPhoto) return true;
    if (this.args.myCloudUploading && !this.isUploadItemMode) return true;
    return !this.sticker && !this.postToShare && !this.isUploadItemMode;
  }

  get canAddVideo() {
    return this.showAddMediaButton && !this.showPhoto;
  }

  get canAddPhoto() {
    return this.showAddMediaButton && !this.showVideo;
  }

  get showAddFileButton() {
    if (this.args.myCloudUploading) return false;

    return (
      !this.sticker &&
      !this.voiceRecordingVisible &&
      !this.postToShare &&
      !this.isUploadItemMode &&
      !this.preselectedAudio
    );
  }

  get showAddStickerButton() {
    if (this.args.myCloudUploading) return false;

    return !this.sticker && !this.selectedGifs.length && !this.isUploadItemMode;
  }

  get showAddGifButton() {
    if (this.args.myCloudUploading) return false;

    return !this.sticker && !this.showPoll && !this.selectedGifs.length && !this.isUploadItemMode;
  }

  get showAddPollButton() {
    if (this.args.myCloudUploading) return false;

    return !this.sticker && !this.selectedGifs.length && !this.postToShare && !this.isUploadItemMode;
  }

  get showVoiceRecordButton() {
    if (this.args.myCloudUploading) return false;

    return !this.voiceRecordingVisible && !this.isUploadItemMode && !this.preselectedAudio;
  }

  get postingController() {
    if (this.photo.photos?.length) return this.postPhoto;
    if (this.documents.length) return this.postDocument;
    if (this.showPoll) return this.postPoll;

    // reposting video is posting using status controller
    if (this.showVideo && !this.preselectedVideo) return this.postVideo;

    return this.postStatus;
  }

  getNewPostToServer(newPost, mentionsStrategy, selectedGifs) {
    const text = this.newPost || '';

    const res = toServer(text + this.getTagsText(), {
      mentionsStrategy: mentionsStrategy,
      gifUrls: selectedGifs,
      parseNativeMarkdown: true,
    });

    return (res || '').trim();
  }

  getTagsText() {
    let tagsText = '';

    if (this.selectedTags.length) {
      this.selectedTags.forEach((tag) => (tagsText += ' #' + tag.name));
    }

    return tagsText;
  }

  @action
  unselectTag(tag) {
    this.selectedTags.removeObject(tag);
  }

  get alreadyUsedTagsCount() {
    return TwitterText.extractHashtagsWithIndices(this.newPost).length;
  }

  maxHashtags = maxHashtags;

  get postToShareTarget() {
    switch (this.target) {
      case Target.GROUP:
        return `group/${this.group?.id}`;

      case Target.PAGE:
        return `page/${this.page?.id}`;

      case Target.EVENT:
        return `event/${this.event?.id}`;

      default:
        return `home`;
    }
  }

  get postToShareScope() {
    let { scope, scopeId } = PostUtils.getPostScopeAndId(this.postToShare);

    switch (scope) {
      case Theme.GROUP:
      case Theme.GROUPS:
        return `/group/${scopeId}`;
      case Theme.PAGE:
      case Theme.PAGES:
        return `/pages/page/${scopeId}`;
      case Theme.EVENT:
        return `/event/${scopeId}`;
      default:
        return `/home`;
    }
  }

  @action
  shareAction() {
    this.share();
  }

  share() {
    if (this.isSharing) return;
    if (this.shareButtonDisabled) return;

    const postToServer = this.getNewPostToServer(this.newPost, this.mentionsStrategy, this.selectedGifs);

    this.newPostServer = postToServer;
    this.doShare();
  }

  doShare() {
    const postOrShare = () => {
      if (this.postToShare && this.includeOriginalPost) {
        // for posting it's set in posting utils share function
        this.isSharing = true;

        const params = {
          newText: this.newPostServer,
          audioId: this.audioFileId,
          postedByPage: !!this.page,
          emojisPreset: this.presetEmojisList,
          commentsDisabled: this.commentsDisabled,
        };

        if (this.postingType === 'everyone') {
          params.everyone = true;
        } else if (this.postingType === 'friends') {
          params.closeFriends = true;
        }

        if (this.sticker) {
          if (this.sticker.package) {
            params.stickers = [this.sticker];
          } else {
            params.aiGeneratedStickerIds = [this.sticker.id];
          }
        }

        if (this.scheduleTimestamp) {
          params.schedule = this.scheduleTimestamp;
        }

        PostApi.reshare({
          params,
          target: this.postToShareTarget,
          scope: this.postToShareScope,
          postItemId: this.postToShare.postItemId,
        })
          .then((data) => {
            if (params.schedule) {
              FunctionalUtils.info(__('Your post has been scheduled'));
            } else {
              const isPostingToCurrentLocation = PostCommons.isPostingToCurrentLocation(
                this.postToShareTarget,
                data.post,
                this.router,
              );

              if (!isPostingToCurrentLocation) {
                if (this.postToShare?.isAlbum) {
                  FunctionalUtils.info(__('Album Shared'));
                } else {
                  FunctionalUtils.info(__('Post Shared'));
                }
              }
            }

            if (data.post) {
              if (this.target === Theme.PAGE) {
                let pageId = data.post && data.post.pageId;

                // we have at this point fetched administrated pages, but with no info about publish status (MW)
                PagesApi.page({ id: pageId })
                  .then((data) => {
                    dispatcher.dispatch('feed', 'addPost', data.post, !data.page.published);
                  })
                  .catch(() => {
                    dispatcher.dispatch('feed', 'addPost', data.post);
                  });
              } else {
                dispatcher.dispatch('feed', 'addPost', data.post);
              }
            }

            this.close();
          })
          .catch((data) => {
            if (data && data.status === 403) {
              FunctionalUtils.error(__("Sorry, this post can't be shared"));
            }
            this.isSharing = false;
          });
      } else {
        const p = { schedule: this.scheduleTimestamp, commentsDisabled: this.commentsDisabled, canAddEmoji: !this.commentsDisabled };

        if (this.postToRepost?.isEditing) {
          p.edit = true;
        }

        this.postingController.shareController(p);
      }
    };

    postOrShare();
  }

  @action
  setAlbumName(value) {
    this._selectedAlbumName = value;
  }

  targetEntity() {
    let target = this.target,
      value,
      idName;

    if (target === Target.EVENT && this.eventId) {
      value = this.eventId;
      idName = 'eventId';
    } else if (target === Target.PAGE && this.pageId) {
      value = this.pageId;
      idName = 'pageId';
    } else if (target === Target.GROUP && this.groupId) {
      value = this.groupId;
      idName = 'groupId';
    }

    if (!value) return null;

    return {
      value: value,
      name: idName,
      hash: {
        [idName]: value,
      },
    };
  }

  /*
   * runs always after posting, either successful or not
   */
  alwaysAfterPosting() {
    if (this.isDestroying || this.isDestroyed) return;
    this.isSharing = false;
  }

  _continue() {
    this.isSharing = false;
  }

  err(message) {
    FunctionalUtils.error(message);
    log('Postbox error');
  }

  //we can check type of file only after upload
  //so we have to initialize dropzone beforehand and then decide how to process file after upload
  initDropzones() {
    const postboxDropZoneEl = this.element.querySelector('.drop-overlay'),
      timeout = this.dropZoneTimeout;

    if (!postboxDropZoneEl) return;

    if (!timeout) {
      // limit to trigger only once while dragover
      postboxDropZoneEl.classList.add('to-drop');
    } else {
      clearTimeout(timeout);
    }

    this.dropZoneTimeout = setTimeout(() => {
      if (this.isDestroyed || this.isDestroying) return;

      this.dropZoneTimeout = null;
      postboxDropZoneEl.classList.remove('to-drop');
    }, 100);
  }

  onBeforeUnload(e) {
    if (this.isEmpty() || this.confirmDialogShown) {
      return;
    }

    let { message } = this.getConfirmCloseMsgs();

    if (e) {
      e.returnValue = message;
    }

    return message;
  }

  isEmpty() {
    const text = (toServer(this.newPost, { parseNativeMarkdown: true }) || '').trim();

    return (
      text === '' &&
      (!this.postingController || this.postingController.isEmpty()) &&
      !this.selectedGifs.length &&
      !this.sticker &&
      !this.showPoll &&
      !this.isAudioRecording &&
      !this.isAudioConverting
    );
  }

  @action
  updateHeight() {
    scheduleOnce('afterRender', this, () => {
      if (this.isDestroying || this.isDestroyed) return;
      this.recalculatePostboxScroll();
    });
  }

  get daysValue() {
    if (this.pollSettings.selectedDays) {
      return __('{count} day', {
        count: this.pollSettings.selectedDays,
      });
    }
    return __('Days');
  }

  get hoursValue() {
    if (this.pollSettings.selectedHours) {
      return __('{count} hour', {
        count: this.pollSettings.selectedHours,
      });
    }
    return __('Hours');
  }

  get minutesValue() {
    if (this.pollSettings.selectedMinutes) {
      return __('{count} minute', {
        count: this.pollSettings.selectedMinutes,
      });
    }
    return __('Minutes');
  }

  get daysDefault() {
    return __('Days');
  }
  get hoursDefault() {
    return __('Hours');
  }
  get minutesDefault() {
    return __('Minutes');
  }

  get showSharingOptions() {
    return this.target === Target.CONTACTS && this.dropdownSharing;
  }

  get isShareWithAvailable() {
    return !this.postToRepost?.isEditing;
  }

  get isShareWithEnabled() {
    // this condition has to be before checking target because for MC there is no target
    if (this.target === Target.MYCLOUD) return false;
    // opened from /groups, /pages where target is not selected initially
    if (this.postboxOpenedWithoutTarget) return true;
    // opened from specific group/event/page - can't change target
    if (this.group || this.event || this.page) return false;
    // not sure what the use case is for this
    if (this.postToRepost?.isEditing) return false;

    return [Target.GROUP, Target.EVENT, Target.CONTACTS].includes(this.target);
  }

  get shareWithDetails() {
    let shareWithText;
    let shareWithIcon;

    switch (this.target) {
      case Target.CONTACTS:
        if (this.postingType === 'friends') {
          return {
            shareWithText: __('Favorites'),
            shareWithIcon: 'star-1',
          };
        }

        if (this.account.activeUser.public) {
          if (this.postingType === 'public') {
            return {
              shareWithText: __('Anyone'),
              shareWithIcon: 'global',
            };
          }
        } else {
          if (this.postingType === 'everyone') {
            return {
              shareWithText: __('Everyone'),
              shareWithIcon: 'people',
            };
          } else {
            return {
              shareWithText: __('My Followers'),
              shareWithIcon: 'user',
            };
          }
        }
        break;
      case Target.GROUP:
        shareWithText = this.group?.name || __('Select a group');
        shareWithIcon = 'category-2';
        break;
      case Target.EVENT:
        shareWithText = this.event?.name || __('Select an event');
        shareWithIcon = 'calendar';
        break;
      case Target.PAGE:
        shareWithText = this.page?.name || __('Select a page');
        shareWithIcon = 'book';
        break;
      case Target.MYCLOUD:
        shareWithText = __('My Cloud');
        shareWithIcon = 'cloud';
        break;
    }

    return { shareWithText, shareWithIcon };
  }

  _newPostHandler() {
    this.recalculatePostboxScroll();
  }

  updateEditor(text) {
    if (this.editor) {
      this.editor.update(text);
    }
  }

  @action
  canScheduleChange() {
    if (!this.canSchedule && this.scheduleVisible) {
      this.hideSchedule();
    }
  }

  get isLimitedMember() {
    return GroupStore.getGroupIfMember(this.groupId)?.role === 'Limited';
  }

  get hasPremium() {
    return this.storeState.hasPremium;
  }

  get canScheduleWithoutModeration() {
    const canSchedule = () => {
      return Object.values({
        homeView: this.target === 'contacts',
        groupViewSelectedGroups: this.target === 'group' && typeof this.group !== 'undefined',
        groupViewScheduledtab: this.target === 'group' && typeof this.groupId !== 'undefined',
        whoCanScheduleInGroups:
          this.group?.role === 'Contributor' || this.group?.role === 'Admin' || this.group?.isOwner,
      }).includes(true);
    };
    const notGroupScope = this.target !== 'group';

    if (notGroupScope) return canSchedule();
    else {
      const selectedGroupPermissions = GroupStore.getState({ id: this.groupId })?.permissions;
      const canPostInSelected = PermissionsUtils.canPostWithoutModeration(selectedGroupPermissions);

      return canPostInSelected && canSchedule();
    }
  }

  get canSchedule() {
    if (!this.showScheduleButton) return false;

    // not a page, then premium is required
    return this.hasPremium || this.target === 'page';
  }

  // show button when user would be able to schedule a post if was a premium user
  get showScheduleButton() {
    if (this.args.myCloudUploading) return false;
    // premium is not required to schedule in Pages, they are already paid
    if (this.target === 'page') return true;

    return !this.eventId && !this.isLimitedMember && this.canScheduleWithoutModeration;
  }

  recalculatePostboxScroll() {
    later(() => {
      if (this.isDestroying || this.isDestroyed) return;
      this.args.recalculatePostboxScroll?.();
    }, 100);
  }

  @action
  setPostingType(type) {
    this.postingType = type;
  }

  @action
  imageLoaded() {
    this.recalculatePostboxScroll();
  }

  @action
  keyUpAction(value, e) {
    this.linkController.parseLinkFunc(e);
    this.recalculatePostboxScroll();
  }

  /************************ PHOTO ************************/

  @action
  toggleMediaDropdown(value) {
    // there is already a photo added and photos can't be mixed with videos
    // so open photo selector right away without showing dropdown
    if (value && this.showPhoto) {
      this.addPhoto();
      return;
    }

    this.setDropdownRefMedia(value);
  }

  @action
  addPhoto() {
    document.getElementById('fake-upload-photo').click();
  }

  @action
  removePhoto(photo) {
    this.postPhoto.removePhoto(photo);
  }

  /************************ GIF ************************/
  @action
  addGif(event) {
    this.dynamicPopups.openPopup('mw-giphy-picker', {
      parent: event.target,
      selectedGifs: this.selectedGifs,
      onClose: () => {
        this.focusTextarea();
        this.updateHeight();
      },
    });
  }

  /************************ FILE ************************/
  @action
  addFile() {
    document.getElementById('fake-upload-document').click();
  }

  @action
  removeFile(file) {
    this.postDocument.removeFile(file);
  }

  @action
  removeFolder() {
    this._folder = false;
  }

  @action
  cancelUploadPreselected(file) {
    A(this.preselectedFiles).removeObject(file);
    this.uploadService.remove(file.id);
  }

  /************************ VIDEO ************************/
  @action
  addVideo() {
    document.getElementById('fake-upload-video').click();
  }

  @action
  cancelVideoUpload() {
    if (this.video.loading || this.video.progress) {
      this.postVideo.removeVideo();
    }
    if (this.uploadXhr) {
      this.uploadXhr.cancel();
    }
  }

  /************************ POLL ************************/
  @action
  addPoll() {
    this.showPoll = true;
    this.updateHeight();
  }

  @action
  cancelPoll() {
    this.showPoll = false;
    this.postPoll.clear();
    this.updateHeight();
  }

  @action
  addPollOption() {
    this.postPoll.addPollOption();
    this.pollContentChanged();
  }

  @action
  addPollPhoto(index) {
    this.currentPollPhotoIndex = index;
    document.getElementById('fake-upload-photo-poll').click();
    this.pollContentChanged();
  }

  @action
  removePollPhoto(index) {
    this.postPoll.removePhoto(index);
    this.pollContentChanged();
  }

  @action
  updatePollQuestion(text) {
    this.pollQuestion = text;
    this.pollContentChanged();
  }

  @action
  updatePollOption(option, text) {
    option.text = text;
    this.pollContentChanged();
  }

  pollContentChanged() {
    this.postPoll.validatePoll();
    this.updateHeight();
  }

  @action
  setDays(days) {
    set(this, 'pollSettings.selectedDays', days);
  }

  @action
  setHours(hours) {
    set(this, 'pollSettings.selectedHours', hours);
  }

  @action
  setMinutes(minutes) {
    set(this, 'pollSettings.selectedMinutes', minutes);
  }

  @action
  cancelVoice() {
    this.audioFileId = null;
    this.audioRecordingUrl = null;
    this.audioRecordingBlob = null;
    this.voiceRecordingVisible = false;
    this.preselectedAudio = null;
    this.updateHeight();
  }

  prefillAudio(audioFile) {
    this.voiceRecordingVisible = true;
    this.audioUploaded = true;
    this.audioRecordingUrl = audioFile._links.url.href;
    this.audioFileId = audioFile.id;

    this.preselectedAudio = audioFile;
  }

  getConfirmCloseMsgs() {
    let message = __(`If you close this pop-up, your post won’t be saved. Do you still want to delete it?`);
    let cancelBtnText = __('Continue editing');
    let okBtnText = __('Delete Post');
    let title = __('Delete Post?');

    return { title, message, okBtnText, cancelBtnText };
  }

  @action
  confirmClose(afterCloseCallback) {
    if (!this.isEmpty()) {
      let { title, message, okBtnText, cancelBtnText } = this.getConfirmCloseMsgs();

      this.confirmDialogShown = true;
      let setConfirmDialogShownToFalse = () => {
        if (!this.isDestroying && !this.isDestroyed) {
          this.confirmDialogShown = false;
        }
      };

      this.dynamicDialogs.openDialog('simple-dialog-new', {
        title: title,
        message: message,
        okButtonText: okBtnText,
        cancelButtonText: cancelBtnText,
        onConfirm: () => {
          setConfirmDialogShownToFalse();
          this.close(afterCloseCallback);
        },
        onCancel: setConfirmDialogShownToFalse,
      });
    } else {
      this.close(afterCloseCallback);
    }
  }

  @action
  close(afterCloseCallback) {
    if (this.isDestroying || this.isDestroyed) return;

    this.uploadService.clear();
    this.uploadService.postboxOpened = false;
    setPostboxOpen(false);

    this.newPost = '';
    this.newPostServer = '';
    this._selectedAlbumName = null;
    this.isSharing = false;

    if (this.postToRepost?.isEditing) {
      this.postToRepost.isEditing = false;
    }

    this.linkController.setInitialLinkProperties();

    if (typeof this.postingController.cleanAfter === 'function') {
      this.postingController.cleanAfter(this);
    }

    this.setDropdownRefSharing(false);
    this.selectedGifs = A();
    this._selectedAlbumName = null;
    this.photoUploadDialog = null;
    this.filesToUpload = A();
    this.showPoll = false;

    this.dynamicDialogs.close(this.args.dialogId, true);

    if (typeof afterCloseCallback === 'function') {
      afterCloseCallback();
    }
  }

  @action
  focusTextarea() {
    if (!this.isDestroying && !this.isDestroyed && this.editor) this.editor.focus();
  }

  @action
  openAudioRecording() {
    this.voiceRecordingVisible = true;
  }

  @action
  toggleSchedule() {
    if (this.canSchedule) {
      this._scheduleVisible = !this._scheduleVisible;
    } else {
      this.dynamicDialogs.openDialog('premium-feature-dialog', { context: this.premiumPreviewTrackingContext });
    }
  }

  @action
  cancelSchedule() {
    this.scheduleTimestamp = null;
    this._scheduleVisible = false;
  }

  @action
  updateScheduleTimestamp(timestamp) {
    this.scheduleTimestamp = timestamp;
  }

  @action
  onSelectTarget(targetObj) {
    switch (this.target) {
      case Target.PAGE:
        this._selectedPage = targetObj;
        this.selectedTargetId = targetObj.id;
        break;

      case Target.GROUP:
        this._selectedGroup = targetObj;
        this.selectedTargetId = targetObj.id;
        break;

      case Target.EVENT:
        this._selectedEvent = targetObj;
        this.selectedTargetId = targetObj.id;
        break;
    }
    this.setAutocomplete();
    this.toggleShareWithDropdown(false);
  }

  @action
  removePresetEmoji(emoji) {
    this.presetEmojis.removeObject(emoji);
  }

  @action
  toggleLinkThumbnail() {
    set(this, 'link.noThumbnail', !this.link.noThumbnail);
  }

  @action
  nextLinkThumbnail() {
    set(this, 'link.thumbnailCounter', MathUtils.mod(this.link.thumbnailCounter + 1, this.link.images?.length));
    set(this, 'link.image', this.link.images[this.link.thumbnailCounter]);
  }

  @action
  prevLinkThumbnail() {
    set(this, 'link.thumbnailCounter', MathUtils.mod(this.link.thumbnailCounter - 1, this.link.images?.length));
    set(this, 'link.image', this.link.images[this.link.thumbnailCounter]);
  }

  @action
  removeLink() {
    this.linkController.removeLink();
  }

  @action
  openStickerPicker(event) {
    if (this.isStickerPickerOpen) return;
    this.isStickerPickerOpen = true;
    this.dynamicPopups.openPopup('mw-stickers-picker', {
      parent: event.target,
      setSticker: this.setSticker,
      aiStickerEnabled: true,
      onClose: () => {
        this.isStickerPickerOpen = false;
      },
    });
  }

  @action
  removeSticker() {
    this.sticker = null;
    this.updateHeight();
  }

  @action
  addEmoji(emoji) {
    this.presetEmojis.pushObject(emoji);
    this.updateHeight();
  }

  @action
  setEmojiPicker(isOpen) {
    this.emojiPickerOpened = isOpen;
  }

  @action
  setSticker(sticker) {
    this.sticker = sticker;
    this.focusTextarea();
    this.updateHeight();
  }

  @action
  photosSorted(idArray) {
    const sortedPhotos = idArray
      .map((id) => {
        return this.photo.photos?.toArray().find((photo) => photo.id === id);
      })
      .filter((photo) => photo);
    this.photo.photos = A(sortedPhotos);
  }

  @action
  setPostBoxProperty(propertyName, value) {
    this[propertyName] = value;
  }

  @action
  setEditor(editor) {
    this.editor = editor;
  }

  @action
  updateContent(propertyName, content) {
    this[propertyName] = content;
  }

  @action
  updateOptionContent(option, content) {
    option.set('text', content);
  }

  @action
  updatePhotoCaption(image, caption) {
    image.caption = caption;
  }

  @action
  updateNewPost(content) {
    //we need this check to avoid update post content two times in the same loop
    if (this.newPost !== content) {
      this.newPost = content;
    }
  }
}
